import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { AlertTriangle, Clock, ShieldOff, ScanLine, XCircle, Inbox } from "lucide-react";

const CARDS = [
  { key: "awaiting_approval", label: "Awaiting Approval", icon: Inbox, color: "amber", route: "/jobs" },
  { key: "overdue_invoices", label: "Overdue Invoices", icon: Clock, color: "red", route: "/billing" },
  { key: "denials", label: "Insurance Denials", icon: ShieldOff, color: "red", route: "/claims" },
  { key: "pending_scans", label: "Pending Scans", icon: ScanLine, color: "blue", route: "/calibration" },
];
const COLOR_MAP = {
  amber: "border-amber-500/30 bg-amber-500/5 text-amber-400",
  red: "border-red-500/30 bg-red-500/5 text-red-400",
  blue: "border-blue-500/30 bg-blue-500/5 text-blue-400",
};

export default function DashboardAlerts() {
  const [alerts, setAlerts] = useState(null);

  useEffect(() => {
    const fetch = () => api.get("/dashboard/alerts").then((r) => setAlerts(r.data)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 60_000);
    return () => clearInterval(id);
  }, []);

  if (!alerts) return null;
  if (alerts.total_alerts === 0) {
    return (
      <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 flex items-center gap-3" data-testid="alerts-empty">
        <div className="w-8 h-8 rounded-lg bg-emerald-500/15 flex items-center justify-center">
          <AlertTriangle className="w-4 h-4 text-emerald-400" />
        </div>
        <div>
          <div className="text-sm font-semibold text-emerald-300">All clear</div>
          <div className="text-xs text-slate-400">No urgent operational alerts. Great job, team!</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-800 bg-[#0B1120] p-5" data-testid="alerts-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Operational Alerts</div>
        </div>
        <div className="text-xs text-slate-500">{alerts.total_alerts} item(s) need attention</div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        {CARDS.map((c) => {
          const item = alerts[c.key];
          const count = item?.count ?? 0;
          if (count === 0) return null;
          const Icon = c.icon;
          return (
            <Link
              key={c.key}
              to={c.route}
              data-testid={`alert-${c.key}`}
              className={`rounded-lg border ${COLOR_MAP[c.color]} p-4 hover:scale-[1.02] transition-transform`}
            >
              <div className="flex items-center justify-between mb-2">
                <Icon className="w-4 h-4" />
                <div className="font-mono font-bold text-2xl">{count}</div>
              </div>
              <div className="text-xs uppercase tracking-wider font-bold opacity-90">{c.label}</div>
              {c.key === "overdue_invoices" && item.total_amount > 0 && (
                <div className="text-[10px] mt-1 opacity-70">${item.total_amount.toLocaleString()} outstanding</div>
              )}
            </Link>
          );
        })}
        {alerts.calibration_failures > 0 && (
          <Link to="/calibration" data-testid="alert-failures" className="rounded-lg border border-red-500/30 bg-red-500/5 text-red-400 p-4 hover:scale-[1.02] transition-transform">
            <div className="flex items-center justify-between mb-2">
              <XCircle className="w-4 h-4" />
              <div className="font-mono font-bold text-2xl">{alerts.calibration_failures}</div>
            </div>
            <div className="text-xs uppercase tracking-wider font-bold opacity-90">Calibration Failures</div>
          </Link>
        )}
      </div>
    </div>
  );
}
